import type { Database } from '@/types/database.types';

type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface TeamMemberSelectorProps {
    members: TeamMember[];
    selectedId: number | null;
    onSelect: (memberId: number | null) => void;
    taskCounts?: Record<number, number>;
    className?: string;
}

/**
 * TeamMemberSelector - Dropdown to select a team member
 * Displays avatar/initials, name, role badge, and optional task count
 * 
 * Satisfies AC 9, 12, 13:
 * - AC9: Team Member Level Timeline is accessible from a team member selection
 * - AC12: Team member header displays: Member avatar/initials, name, role badge, total task count
 * - AC13: Member selector dropdown shows all team members with avatars, names, roles, and current task counts
 */
export const TeamMemberSelector = ({
    members,
    selectedId,
    onSelect,
    taskCounts = {},
    className = '',
}: TeamMemberSelectorProps) => {
    const selectedMember = members.find((m) => m.id === selectedId);

    const getInitials = (name: string): string => {
        const trimmed = name.trim();
        if (!trimmed) return '?';
        const parts = trimmed.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0]?.[0] || ''}${parts[parts.length - 1]?.[0] || ''}`.toUpperCase();
        }
        return trimmed.substring(0, 2).toUpperCase() || '?';
    };

    const getRoleBadgeColor = (role: TeamMember['role']): string => {
        switch (role) {
            case 'developer': return 'bg-blue-100 text-blue-800';
            case 'qa': return 'bg-emerald-100 text-emerald-800';
        }
    };

    const getRoleLabel = (role: TeamMember['role']): string => {
        switch (role) {
            case 'developer': return 'Developer';
            case 'qa': return 'QA';
        }
    };

    return (
        <div className={`team-member-selector ${className}`}>
            <label htmlFor="member-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Team Member
            </label>
            <select
                id="member-select"
                value={selectedId || ''}
                onChange={(e) => {
                    const value = e.target.value;
                    onSelect(value ? parseInt(value, 10) : null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
                <option value="">-- Select a team member --</option>
                {members.map((member) => {
                    const taskCount = taskCounts[member.id] || 0;
                    return (
                        <option key={member.id} value={member.id}>
                            {member.name} ({getRoleLabel(member.role)}) - {taskCount} task{taskCount !== 1 ? 's' : ''}
                        </option>
                    );
                })}
            </select>

            {/* Selected Member Header */}
            {selectedMember && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-4">
                        {/* Avatar with initials */}
                        <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-semibold">
                            {getInitials(selectedMember.name)}
                        </div>

                        {/* Member info */}
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {selectedMember.name}
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                                {/* Role badge */}
                                <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(
                                        selectedMember.role
                                    )}`}
                                >
                                    {getRoleLabel(selectedMember.role)}
                                </span>

                                {/* Task count */}
                                <span className="text-sm text-gray-600">
                                    {taskCounts[selectedMember.id] || 0} task
                                    {(taskCounts[selectedMember.id] || 0) !== 1 ? 's' : ''} assigned
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

